import React from 'react';
import ScheduleBoardCell from './ScheduleBoardCell';
import { format, isToday, isWeekend } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { CheckSquare, RotateCcw } from 'lucide-react';
import { useBoardColumnWidth } from '../../hooks/useBoardColumnWidth';

const WC_ROW_COLORS = [
  { bg: 'bg-rose-50', border: 'border-rose-100', text: 'text-rose-800' },
  { bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-800' },
  { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-800' },
  { bg: 'bg-lime-50', border: 'border-lime-100', text: 'text-lime-800' },
  { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-800' },
  { bg: 'bg-cyan-50', border: 'border-cyan-100', text: 'text-cyan-800' },
  { bg: 'bg-sky-50', border: 'border-sky-100', text: 'text-sky-800' },
  { bg: 'bg-violet-50', border: 'border-violet-100', text: 'text-violet-800' },
  { bg: 'bg-fuchsia-50', border: 'border-fuchsia-100', text: 'text-fuchsia-800' },
  { bg: 'bg-pink-50', border: 'border-pink-100', text: 'text-pink-800' },
];

function ScheduleBoardGrid({ dates, workCenters, schedulesByCell, dailyStats, settledDateMap, scheduleChildrenMap, scheduleDescendantCountMap, scheduleOrderTotalsMap, containerWidth, onDeleteSchedule, onSplitSchedule, onWithdrawSplit, onExportProcess, onDailySettle, onReverseDailySettle, onEditNote, onQuickAdd }) {
  const { dateColWidth, firstColWidth } = useBoardColumnWidth();
  if (workCenters.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400">
        暂无设备数据
      </div>
    );
  }

  const getDateStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const stats = dailyStats?.[dateStr];
    if (!stats || stats.taskCount === 0) return { label: '无计划', variant: 'outline', color: 'text-gray-400' };
    if (settledDateMap?.[dateStr]) return { label: '已结算', variant: 'default', color: 'text-green-700' };
    if (stats.inProgress) return { label: '进行中', variant: 'secondary', color: 'text-amber-700' };
    return { label: '计划中', variant: 'outline', color: 'text-blue-600' };
  };

  const gridStyle = {
    '--date-col-width': `${dateColWidth}px`,
    '--first-col-width': `${firstColWidth}px`,
    gridTemplateColumns: `var(--first-col-width) repeat(${dates.length}, var(--date-col-width))`,
    minWidth: `calc(var(--first-col-width) + ${dates.length} * var(--date-col-width))`,
    alignItems: 'stretch',
  };

  return (
    <div
      className="h-full overflow-auto min-w-0"
      style={{ width: containerWidth || '100%' }}
    >
      <div className="grid" style={gridStyle}>
        {/* Header: fixed equipment/date cell */}
        <div className="border-r border-b border-gray-200 px-2 py-2 text-xs font-semibold text-gray-600 flex items-center bg-white sticky top-0 left-0 z-20">
          设备 / 日期
        </div>

        {/* Header: date columns */}
        {dates.map((date) => {
          const today = isToday(date);
          const weekend = isWeekend(date);
          const dateStr = format(date, 'yyyy-MM-dd');
          const stats = dailyStats?.[dateStr] || { totalAssigned: 0, totalCompleted: 0, totalScrapped: 0, taskCount: 0 };
          const status = getDateStatus(date);

          return (
            <div
              key={date.toISOString()}
              className={`
                px-1 sm:px-2 py-1.5 sm:py-2 text-center border-r border-b border-gray-200 sticky top-0
                ${today ? 'bg-blue-50' : weekend ? 'bg-gray-50' : 'bg-white'}
              `}
            >
              <div className={`text-[10px] sm:text-xs font-semibold ${today ? 'text-blue-700' : 'text-gray-700'}`}>
                {format(date, 'MM/dd')}
              </div>
              <div className={`text-[9px] sm:text-[10px] ${today ? 'text-blue-500' : 'text-gray-400'}`}>
                {format(date, 'EEE', { locale: zhCN })}
                {today && ' · 今天'}
              </div>
              <div className="mt-1 flex justify-center">
                <Badge variant={status.variant} className="text-[9px] h-4 px-1">
                  {status.label}
                </Badge>
              </div>
              {stats.taskCount > 0 && (
                <div className="mt-0.5 sm:mt-1 space-y-0.5">
                  <div className="text-[9px] text-gray-500">
                    {stats.taskCount}项 · 计{stats.totalAssigned}件
                  </div>
                  <div className="flex justify-center gap-0.5 sm:gap-1 text-[9px]">
                    <span className="text-green-600">完{stats.totalCompleted}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-red-500">废{stats.totalScrapped}</span>
                  </div>
                </div>
              )}
              {stats.taskCount > 0 && (
                <div className="mt-1 sm:mt-1.5">
                  {settledDateMap?.[dateStr] ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px] px-1.5 sm:px-2 py-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReverseDailySettle?.(date);
                      }}
                    >
                      <RotateCcw className="h-3 w-3 sm:mr-0.5" />
                      <span className="hidden sm:inline">撤销结算</span>
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px] px-1.5 sm:px-2 py-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDailySettle?.(date);
                      }}
                    >
                      <CheckSquare className="h-3 w-3 sm:mr-0.5" />
                      <span className="hidden sm:inline">结算</span>
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Rows: one grid row per work center */}
        {workCenters.map((wc, idx) => {
          const color = WC_ROW_COLORS[idx % WC_ROW_COLORS.length];
          return (
            <React.Fragment key={wc._id}>
              {/* Fixed equipment name cell */}
              <div className={`${color.bg} border-r ${color.border} border-b px-2 py-2 flex items-center sticky left-0 z-10`} style={{ minHeight: 140 }}>
                <div className="min-w-0">
                  <div className={`text-xs font-semibold ${color.text} truncate`}>{wc.name}</div>
                  <div className="text-[10px] text-gray-500">{wc.work_center_id}</div>
                </div>
              </div>

              {/* Date cells */}
              {dates.map((date) => {
                const dateKey = format(date, 'yyyy-MM-dd');
                const cellKey = `${wc._id}_${dateKey}`;
                const cellSchedules = schedulesByCell[cellKey] || [];
                return (
                  <ScheduleBoardCell
                    key={cellKey}
                    date={date}
                    workCenter={wc}
                    schedules={cellSchedules}
                    settledDateMap={settledDateMap}
                    scheduleChildrenMap={scheduleChildrenMap}
                    scheduleDescendantCountMap={scheduleDescendantCountMap}
                    scheduleOrderTotalsMap={scheduleOrderTotalsMap}
                    onDeleteSchedule={onDeleteSchedule}
                    onSplitSchedule={onSplitSchedule}
                    onWithdrawSplit={onWithdrawSplit}
                    onExportProcess={onExportProcess}
                    onEditNote={onEditNote}
                    onQuickAdd={onQuickAdd}
                    rowColor={color}
                  />
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default ScheduleBoardGrid;
